import React from 'react';
import {APP_STORE_URL} from '../../constants/app';
import {GeoHubRestaurant} from '../../data/geoHubData';
import {buildSupabaseImageUrl} from '../../utils/supabaseImage';
import GeoHubLocalNote from './GeoHubLocalNote';

type GeoHubRestaurantGridProps = {
    cityName: string;
    restaurants: GeoHubRestaurant[];
};

const GeoHubRestaurantGrid: React.FC<GeoHubRestaurantGridProps> = ({
    cityName,
    restaurants,
}) => {
    return (
        <section className="geo-hub-grid-section" aria-labelledby="geo-hub-grid-heading">
            <div className="geo-hub-grid-section__head">
                <h2 id="geo-hub-grid-heading">Places to eat in {cityName}</h2>
                <GeoHubLocalNote cityName={cityName}/>
            </div>

            <ul className="geo-hub-grid">
                {restaurants.map((r) => (
                    <GeoHubCard key={r.soltPath ?? r.name} cityName={cityName} restaurant={r}/>
                ))}
            </ul>
        </section>
    );
};

type GeoHubCardProps = {
    cityName: string;
    restaurant: GeoHubRestaurant;
};

const GeoHubCard: React.FC<GeoHubCardProps> = ({cityName, restaurant}) => {
    const rawImage = restaurant.dishImage ?? restaurant.restaurantImage;
    const imageSrc = rawImage
        ? buildSupabaseImageUrl(rawImage, {width: 640, height: 480, quality: 80, resize: 'cover'})
        : undefined;
    const className = 'geo-hub-card geo-hub-card--link';
    const eats = restaurant.postsCount ?? 0;
    const showRating = restaurant.starRating != null && restaurant.starRating > 0;

    return (
        <li>
            <a
                href={APP_STORE_URL}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
            >
                <div className={`geo-hub-card__media${!imageSrc ? ' geo-hub-card__media--empty' : ''}`}>
                    {imageSrc ? (
                        <img
                            src={imageSrc}
                            alt={`Dish at ${restaurant.name}, ${cityName}`}
                            loading="lazy"
                            decoding="async"
                        />
                    ) : (
                        <span className="geo-hub-card__placeholder" aria-hidden="true"/>
                    )}
                    {eats > 0 && (
                        <span className="geo-hub-card__posts">
                            {eats} {eats === 1 ? 'eat' : 'eats'}
                        </span>
                    )}
                </div>
                <div className="geo-hub-card__body">
                    <div className="geo-hub-card__title-row">
                        <h3 title={restaurant.name}>{restaurant.name}</h3>
                        {showRating && (
                            <span
                                className="geo-hub-card__rating"
                                aria-label={`${restaurant.starRating} stars`}
                            >
                                ★ {restaurant.starRating!.toFixed(1)}
                            </span>
                        )}
                    </div>
                    <span className="geo-hub-card__cta">See eats and prices on Solt</span>
                </div>
            </a>
        </li>
    );
};

export default GeoHubRestaurantGrid;
